import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Orders, Catalog,
  type PurchaseOrderLine, type PurchaseOrderStatus,
} from '@/lib/api';
import {
  Badge, Button, Card, EmptyState, IconButton, Input, Modal, Skeleton,
} from '@/design-system';
import { formatPriceNok, formatDate } from '@/lib/format';
import { useToasts } from '@/lib/store';
import { useDebounce } from '@/lib/hooks';
import { LocationPicker } from '@/components/LocationPicker';
import { BottleImage } from '@/components/BottleCard';
import { UpgradeGate, isEntitlementError } from '@/components/UpgradeGate';

const STATUS_TONE: Record<PurchaseOrderStatus, 'mist' | 'gold' | 'success' | 'danger'> = {
  draft: 'mist',
  ordered: 'gold',
  received: 'success',
  cancelled: 'danger',
};

/**
 * Bestilling ("purchase orders") — draft → ordered → received/cancelled.
 * Master list on top, editor for the selected order below. Placing an
 * order creates one pending-delivery bottle per line; receiving assigns a
 * location and clears pendingDelivery — see backend/src/routes/orders.ts.
 */
export function BestillingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const ordersQ = useQuery({ queryKey: ['orders'], queryFn: () => Orders.list() });

  const createOrder = useMutation({
    mutationFn: () => Orders.create({}),
    onSuccess: (order) => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      setSelectedId(order.id);
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  if (isEntitlementError(ordersQ.error)) {
    return <UpgradeGate error={ordersQ.error} featureLabel="Bestilling" />;
  }

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">{t('orders.title')}</h1>
          <p className="text-sm text-mist">{t('orders.subtitle', { count: ordersQ.data?.length ?? 0 })}</p>
        </div>
        <Button iconLeft="plus" loading={createOrder.isPending} onClick={() => createOrder.mutate()}>
          {t('orders.new')}
        </Button>
      </header>

      {ordersQ.isLoading ? (
        <Skeleton height={160} rounded="md" />
      ) : !ordersQ.data?.length ? (
        <EmptyState icon="truck" title={t('orders.empty')} />
      ) : (
        <div className="grid gap-2">
          {ordersQ.data.map((order) => (
            <button
              key={order.id}
              type="button"
              onClick={() => setSelectedId(order.id)}
              className={`ui-card flex flex-wrap items-center justify-between gap-3 p-3 text-left transition-colors ${
                selectedId === order.id ? 'border-candle bg-candle/5' : 'hover:border-line-soft'
              }`}
            >
              <div className="grid gap-0.5">
                <span className="font-serif text-parchment">{order.supplier || t('orders.noSupplier')}</span>
                <span className="text-xs text-mist">
                  {formatDate(order.createdAt)}
                  {order.note ? ` · ${order.note}` : ''}
                </span>
              </div>
              <Badge tone={STATUS_TONE[order.status]}>{t(`orders.status.${order.status}`)}</Badge>
            </button>
          ))}
        </div>
      )}

      {selectedId && <OrderEditor orderId={selectedId} onClose={() => setSelectedId(undefined)} />}
    </div>
  );
}

interface LineDraft {
  catalogId?: string;
  name: string;
  producer?: string;
  vintage?: string;
  country?: string;
  region?: string;
  imageUrl?: string;
  quantity: number;
  unitPriceNok?: number;
}

const EMPTY_LINE_DRAFT: LineDraft = { name: '', quantity: 6 };

function OrderEditor({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const [lineDraft, setLineDraft] = useState<LineDraft>(EMPTY_LINE_DRAFT);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveLocationId, setReceiveLocationId] = useState<string | undefined>(undefined);

  const detailQ = useQuery({ queryKey: ['orders', orderId], queryFn: () => Orders.get(orderId) });
  const catalogQ = useQuery({
    queryKey: ['orders.catalog.search', debounced],
    queryFn: () => Catalog.search(debounced, 8),
    enabled: debounced.length >= 2,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['orders', orderId] });
  };

  const addLine = useMutation({
    mutationFn: () =>
      Orders.addLine(orderId, {
        catalogId: lineDraft.catalogId,
        name: lineDraft.name,
        producer: lineDraft.producer,
        vintage: lineDraft.vintage,
        country: lineDraft.country,
        region: lineDraft.region,
        imageUrl: lineDraft.imageUrl,
        quantity: lineDraft.quantity,
        unitPriceNok: lineDraft.unitPriceNok,
      }),
    onSuccess: () => {
      setLineDraft(EMPTY_LINE_DRAFT);
      setSearch('');
      invalidate();
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => Orders.deleteLine(orderId, lineId),
    onSuccess: invalidate,
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const place = useMutation({
    mutationFn: () => Orders.place(orderId),
    onSuccess: () => {
      push({ title: t('orders.placed'), tone: 'success' });
      invalidate();
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const receive = useMutation({
    mutationFn: () => Orders.receive(orderId, { locationId: receiveLocationId }),
    onSuccess: () => {
      push({ title: t('orders.received'), tone: 'success' });
      setReceiveOpen(false);
      invalidate();
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      void qc.invalidateQueries({ queryKey: ['locations.tree'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const cancel = useMutation({
    mutationFn: () => Orders.cancel(orderId),
    onSuccess: () => {
      push({ title: t('orders.cancelled'), tone: 'success' });
      invalidate();
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const order = detailQ.data?.order;
  const lines: PurchaseOrderLine[] = detailQ.data?.lines ?? [];
  const total = lines.reduce((sum, line) => sum + line.quantity * (line.unitPriceNok ?? 0), 0);
  const isDraft = order?.status === 'draft';

  return (
    <Card className="grid gap-5 p-5">
      <div className="flex items-center justify-between">
        <h2 className="ui-card-title">{order?.supplier || t('orders.noSupplier')}</h2>
        <Button variant="ghost" size="sm" iconLeft="x" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>

      {detailQ.isLoading || !order ? (
        <Skeleton height={200} rounded="md" />
      ) : (
        <>
          {order.status !== 'draft' && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-mist">
              <Badge tone={STATUS_TONE[order.status]}>{t(`orders.status.${order.status}`)}</Badge>
              {order.orderedAt && <span>{t('orders.orderedAt', { date: formatDate(order.orderedAt) })}</span>}
              {order.receivedAt && <span>{t('orders.receivedAt', { date: formatDate(order.receivedAt) })}</span>}
            </div>
          )}

          {isDraft && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('orders.supplier')}
                defaultValue={order.supplier ?? ''}
                onBlur={(e) => {
                  if (e.target.value !== (order.supplier ?? '')) {
                    Orders.update(orderId, { supplier: e.target.value }).then(invalidate).catch(() => undefined);
                  }
                }}
              />
              <Input
                label={t('orders.note')}
                defaultValue={order.note ?? ''}
                onBlur={(e) => {
                  if (e.target.value !== (order.note ?? '')) {
                    Orders.update(orderId, { note: e.target.value }).then(invalidate).catch(() => undefined);
                  }
                }}
              />
            </div>
          )}

          <div className="grid gap-2">
            {lines.map((line) => (
              <div key={line.id} className="ui-card flex items-center gap-3 p-2.5">
                <div className="relative grid h-16 w-12 shrink-0 place-items-center overflow-hidden rounded-md bg-ink-soft">
                  <BottleImage src={line.imageUrl} iconSize={20} />
                </div>
                <div className="grid min-w-0 flex-1">
                  <span className="truncate font-serif text-sm text-parchment">
                    {[line.vintage, line.name].filter(Boolean).join(' ')}
                  </span>
                  <span className="truncate text-[10px] uppercase tracking-widest text-mist">
                    {[line.producer, line.region ?? line.country].filter(Boolean).join(' · ') || '—'}
                  </span>
                </div>
                <span className="w-10 shrink-0 text-right text-sm text-mist">×{line.quantity}</span>
                <span className="w-20 shrink-0 text-right text-sm text-parchment">{formatPriceNok(line.unitPriceNok)}</span>
                <span className="w-24 shrink-0 text-right text-sm text-parchment">
                  {formatPriceNok(line.quantity * (line.unitPriceNok ?? 0))}
                </span>
                {isDraft && (
                  <IconButton icon="trash" label={t('common.delete')} size="sm" onClick={() => removeLine.mutate(line.id)} />
                )}
              </div>
            ))}
            {lines.length === 0 && <p className="py-4 text-center text-sm text-mist">{t('orders.noLines')}</p>}
          </div>

          {lines.length > 0 && (
            <div className="flex items-center justify-between border-t border-line pt-3">
              <span className="text-xs uppercase tracking-widest text-mist">{t('orders.total')}</span>
              <span className="font-serif text-lg text-parchment">{formatPriceNok(total)}</span>
            </div>
          )}

          {isDraft && <div className="ui-divider" />}

          {isDraft && (
            <section className="grid gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('orders.addLine')}</h3>
              <Input
                iconLeft="search"
                placeholder={t('orders.searchCatalog')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {catalogQ.data && catalogQ.data.products.length > 0 && (
                <div className="grid max-h-48 gap-1.5 overflow-y-auto pr-1">
                  {catalogQ.data.products.map((p) => {
                    const name = p.basic.productLongName ?? p.basic.productShortName ?? '—';
                    const country = (p.origins as { country?: string } | undefined)?.country;
                    const region = (p.origins as { region?: string } | undefined)?.region;
                    return (
                      <button
                        key={p.basic.productId}
                        type="button"
                        className="ui-card flex items-center gap-2 p-2 text-left hover:border-line-soft"
                        onClick={() =>
                          setLineDraft((d) => ({
                            ...d,
                            catalogId: p.basic.productId,
                            name,
                            vintage: p.basic.vintage,
                            country,
                            region,
                            imageUrl: p.imageUrl,
                          }))
                        }
                      >
                        <span className="min-w-0 flex-1 truncate text-sm text-parchment">{name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-mist">{p.basic.vintage ?? '—'}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-3">
                <Input
                  label={t('orders.lineName')}
                  value={lineDraft.name}
                  onChange={(e) => setLineDraft((d) => ({ ...d, name: e.target.value }))}
                />
                <Input
                  label={t('orders.quantity')}
                  type="number"
                  min={1}
                  value={lineDraft.quantity}
                  onChange={(e) => setLineDraft((d) => ({ ...d, quantity: Number(e.target.value) }))}
                />
                <Input
                  label={t('orders.unitPrice')}
                  type="number"
                  min={0}
                  value={lineDraft.unitPriceNok ?? ''}
                  onChange={(e) =>
                    setLineDraft((d) => ({ ...d, unitPriceNok: e.target.value === '' ? undefined : Number(e.target.value) }))
                  }
                />
              </div>
              <Button
                size="sm"
                iconLeft="plus"
                disabled={!lineDraft.name}
                loading={addLine.isPending}
                onClick={() => addLine.mutate()}
              >
                {t('orders.addLineButton')}
              </Button>
            </section>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
            {order.status === 'draft' && (
              <Button iconLeft="truck" loading={place.isPending} disabled={lines.length === 0} onClick={() => place.mutate()}>
                {t('orders.place')}
              </Button>
            )}
            {order.status === 'ordered' && (
              <Button iconLeft="check" onClick={() => setReceiveOpen(true)}>
                {t('orders.markReceived')}
              </Button>
            )}
            {(order.status === 'draft' || order.status === 'ordered') && (
              <Button variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                {t('orders.cancel')}
              </Button>
            )}
          </div>
        </>
      )}

      <Modal
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        title={t('orders.markReceived')}
        footer={
          <Button loading={receive.isPending} onClick={() => receive.mutate()}>
            {t('orders.confirmReceived')}
          </Button>
        }
      >
        <div className="grid gap-3">
          <p className="text-sm text-mist">{t('orders.receiveHint')}</p>
          <LocationPicker value={receiveLocationId} onChange={setReceiveLocationId} />
        </div>
      </Modal>
    </Card>
  );
}
